import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import styles from './MicrositeDomains.module.css'
import { BASE_URL_API } from '../../../References/URLs'
import AuthContext from '../../../store/auth-context'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import { Check, AlertCircle, Plus, RefreshCw, Star } from 'lucide-react'

import MicrositePageHeader from '../components/MicrositePageHeader/MicrositePageHeader'
import MicrositeTable from '../components/MicrositeTable/MicrositeTable'
import MicrositeDomainTypeBadge from '../components/MicrositeDomainTypeBadge/MicrositeDomainTypeBadge'
import MicrositeSslStatusBadge from '../components/MicrositeSslStatusBadge/MicrositeSslStatusBadge'
import MicrositeDnsVerifiedIcon from '../components/MicrositeDnsVerifiedIcon/MicrositeDnsVerifiedIcon'
import MicrositeDnsInstructionsCard from '../components/MicrositeDnsInstructionsCard/MicrositeDnsInstructionsCard'
import MicrositeDeleteConfirm from '../components/MicrositeDeleteConfirm/MicrositeDeleteConfirm'
import MicrositeInlineEditRow from '../components/MicrositeInlineEditRow/MicrositeInlineEditRow'
import TextInput from '../components/TextInput/TextInput'

const MicrositeDomains = () => {
    const authCtx = useContext(AuthContext)
    const asCtx   = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [loading, setLoading] = useState(true)
    const [toast, setToast] = useState(null)
    const [rows, setRows] = useState([])

    const [adding, setAdding] = useState(false)
    const [newDomain, setNewDomain] = useState('')
    const [savingNew, setSavingNew] = useState(false)
    const [pendingInstructions, setPendingInstructions] = useState(null)

    const [verifyingId, setVerifyingId] = useState(null)

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 3000)
    }

    const post = useCallback(async (resource, payload = {}) => {
        try {
            const res = await fetch(BASE_URL_API + resource, {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    vendorIdentifier: JSON.parse(authCtx?.loginData)?.partnersData?.vendorIdentifier,
                    ...payload
                })
            })
            if (!res.ok) return null
            const data = await res.json()
            if (data?.message === 'tokenExpired') {
                authCtx.logout()
                return null
            }
            return data
        } catch {
            return null
        }
    }, [authCtx, activeStore])

    const loadDomains = useCallback(async () => {
        setLoading(true)
        const res = await post('micrositeGetDomains')
        if (res?.status === 'success') {
            setRows(res.response?.data ?? [])
        } else {
            showToast('Could not load domains. Please refresh.', 'error')
        }
        setLoading(false)
    }, [post])

    useEffect(() => {
        if (activeStore?.storeIdentifier) loadDomains()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier])

    const handleAddDomain = async () => {
        if (!newDomain.trim()) {
            showToast('Please enter a domain', 'error')
            return
        }
        setSavingNew(true)
        const res = await post('micrositeAddDomain', { domain: newDomain.trim() })
        setSavingNew(false)

        if (res?.status === 'success') {
            showToast('Domain added — follow the DNS instructions to verify')
            setPendingInstructions(res.response?.dnsInstructions || null)
            setAdding(false)
            setNewDomain('')
            loadDomains()
        } else {
            showToast('Could not add domain', 'error')
        }
    }

    const handleVerify = async (row) => {
        setVerifyingId(row.ID)
        const res = await post('micrositeVerifyDomain', { ID: row.ID })
        setVerifyingId(null)

        if (res?.status === 'success' && res.response?.dnsVerified) {
            showToast('Domain verified successfully')
            loadDomains()
        } else if (res?.status === 'success') {
            showToast('DNS record not found yet — it can take a few hours to propagate', 'error')
        } else {
            showToast('Could not verify domain', 'error')
        }
    }

    const handleSetPrimary = async (row) => {
        const res = await post('micrositeSetPrimaryDomain', { ID: row.ID })
        if (res?.status === 'success') {
            showToast(`${row.domain} is now your primary domain`)
            loadDomains()
        } else {
            showToast('Could not set primary domain', 'error')
        }
    }

    const handleDelete = async (ID) => {
        const res = await post('micrositeDeleteDomain', { ID })
        if (res?.status === 'success') {
            setRows((prev) => prev.filter((r) => r.ID !== ID))
            showToast('Domain removed')
        } else {
            showToast('Could not remove domain', 'error')
        }
    }

    const columns = [
        {
            key: 'domainName',
            label: 'Domain',
            render: (row) => (
                <div className={styles.domainCell}>
                    <span className={styles.domainName}>{row.domain}</span>
                    {row.isPrimary && <Star size={12} className={styles.primaryStar} />}
                </div>
            )
        },
        {
            key: 'domainType',
            label: 'Type',
            render: (row) => <MicrositeDomainTypeBadge value={row.domainType} />
        },
        {
            key: 'dnsVerified',
            label: 'DNS',
            render: (row) => <MicrositeDnsVerifiedIcon verified={!!row.dnsVerified} />
        },
        {
            key: 'sslStatus',
            label: 'SSL',
            render: (row) => <MicrositeSslStatusBadge value={row.sslStatus} />
        },
        {
            key: 'actions',
            label: '',
            align: 'right',
            render: (row) => (
                <div className={styles.rowActions}>
                    {!row.dnsVerified && (
                        <button
                            type="button"
                            className={styles.verifyBtn}
                            onClick={() => handleVerify(row)}
                            disabled={verifyingId === row.ID}
                        >
                            <RefreshCw size={12} className={verifyingId === row.ID ? styles.spinning : ''} />
                            {verifyingId === row.ID ? 'Verifying…' : 'Verify'}
                        </button>
                    )}
                    {row.dnsVerified && !row.isPrimary && (
                        <button type="button" className={styles.primaryBtn} onClick={() => handleSetPrimary(row)}>
                            Make primary
                        </button>
                    )}
                    {!row.isPrimary && <MicrositeDeleteConfirm mode="inline" onConfirm={() => handleDelete(row.ID)} />}
                </div>
            )
        }
    ]

    return (
        <div className={styles.page}>
            {toast && (
                <div className={`${styles.toast} ${styles[`toast_${toast.type}`]}`}>
                    {toast.type === 'success' ? <Check size={14} /> : <AlertCircle size={14} />}
                    {toast.message}
                </div>
            )}

            <MicrositePageHeader
                title="Domains"
                subtitle="Connect a custom domain or use your free Lokaci subdomain."
                primaryAction={{ label: 'Add domain', icon: <Plus size={14} />, onClick: () => setAdding(true) }}
            />

            {pendingInstructions && (
                <div className={styles.instructionsWrap}>
                    <MicrositeDnsInstructionsCard instructions={pendingInstructions} />
                </div>
            )}

            {adding && (
                <MicrositeInlineEditRow onSave={handleAddDomain} onCancel={() => { setAdding(false); setNewDomain('') }} saving={savingNew}>
                    <TextInput
                        label="Domain name"
                        value={newDomain}
                        onChange={setNewDomain}
                        placeholder="www.yoursalon.com"
                    />
                </MicrositeInlineEditRow>
            )}

            <MicrositeTable
                columns={columns}
                rows={rows}
                getRowKey={(row) => row.ID}
                loading={loading}
                emptyMessage="No domains yet"
                emptyDescription="Add a custom domain to use your own URL"
            />
        </div>
    )
}

export default MicrositeDomains